import { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { formatClock } from '@bookingdei/i18n/format-date';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { categoryTileIcon } from '../offers/category-tile-icon';
import { HeroServicePanel, type ServiceRow, type ServiceTab } from './hero-service-panel';
import { HeroLocationPanel, type PlaceOption } from './hero-location-panel';
import { HeroWhenPanel, type WhenValue } from './hero-when-panel';
import { pushRecentLocation, readRecentLocations, saveRecentLocations } from './recent-locations';
import { useDateLabel } from './use-date-label';
import type { MobileStep } from './mobile-search-flow';

// DC123: the phone-only search flow opens on a tap — split out of the entry chunk.
const MobileSearchFlow = lazy(() =>
  import('./mobile-search-flow').then((m) => ({ default: m.MobileSearchFlow })),
);

type Category = components['schemas']['Category'];
type BusinessSummary = components['schemas']['BusinessSummary'];
type Professional = components['schemas']['Professional'];
type SearchResults = components['schemas']['SearchResults'];
type ProfessionalList = components['schemas']['ProfessionalList'];

type Segment = 'service' | 'loc' | 'when';

const SearchIcon = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
);
export const PinIcon = ({ size = 17, stroke = 2.2 }: { size?: number; stroke?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-7-5.6-7-11a7 7 0 1 1 14 0c0 5.4-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.6" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
    <line x1="3.5" y1="10" x2="20.5" y2="10" />
    <line x1="8" y1="2.8" x2="8" y2="6.6" />
    <line x1="16" y1="2.8" x2="16" y2="6.6" />
  </svg>
);

// The hero pill search (design DC:283-445): service, location and when segments, each
// opening its own panel, plus Search. On phones the pill is replaced by one tappable
// bar that opens the full-screen flow (a three-segment pill cannot work at 360 px) —
// the same values and rows, drawn one step per screen (mobile-search-flow.tsx, DC56).
export function HeroSearch({ categories, places }: { categories: Category[]; places: PlaceOption[] }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const dateLabel = useDateLabel();
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const now = useMemo(() => new Date(), []);

  const [open, setOpen] = useState<Segment | null>(null);
  // The phone flow's current step; null while it is closed.
  const [mobileStep, setMobileStep] = useState<MobileStep | null>(null);
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [place, setPlace] = useState<PlaceOption | null>(null);
  const [when, setWhen] = useState<WhenValue>({ date: '', chip: 'any', from: 9, to: 17 });
  const [calMonth, setCalMonth] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [tab, setTab] = useState<ServiceTab>('all');
  const [kb, setKb] = useState(-1);
  const [locFilter, setLocFilter] = useState('');
  const [recentIds, setRecentIds] = useState<string[]>(() => readRecentLocations());
  const [biz, setBiz] = useState<{ items: BusinessSummary[]; total: number }>({ items: [], total: 0 });
  const [pros, setPros] = useState<{ items: Professional[]; total: number }>({ items: [], total: 0 });

  // Close on an outside press or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Businesses + professionals for the service panel: server-side search, refreshed
  // as the query changes (debounced), only while the panel is open.
  const serviceOpen = open === 'service' || mobileStep === 'service';
  useEffect(() => {
    if (!serviceOpen) return;
    const controller = new AbortController();
    const q = query.trim();
    const timer = setTimeout(() => {
      api
        .GET('/search', { params: { query: { ...(q ? { q } : {}), limit: 6 } }, signal: controller.signal, lang })
        .then((r: SearchResults) => setBiz({ items: r.items ?? [], total: r.total_estimate ?? r.items?.length ?? 0 }))
        .catch(() => undefined);
      api
        .GET('/professionals', { params: { query: q ? { q } : {} }, signal: controller.signal, lang })
        .then((r: ProfessionalList) => setPros({ items: r.items ?? [], total: r.total_estimate ?? r.items?.length ?? 0 }))
        .catch(() => undefined);
    }, 150);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [serviceOpen, query, lang]);

  const catName = (c: Category) =>
    lang === 'bn' ? (c.label_bn ?? c.label_en) : (c.menu_label_en ?? c.label_en);
  const q = query.trim().toLowerCase();

  // Panel rows in display order — the list the keyboard walks.
  const rows: ServiceRow[] = [];
  if (tab === 'all' || tab === 'cats') {
    const catRows: ServiceRow[] = [
      {
        key: 'c-all',
        kind: 'cat',
        label: t('dc50.hero.all_services'),
        glyph: '✦',
        tint: 'var(--accent-tint)',
        glyphColor: 'var(--accent-txt)',
        onPick: () => pickCategory(null),
      },
      ...categories.map<ServiceRow>((c) => ({
        key: `c-${c.id}`,
        kind: 'cat',
        label: catName(c),
        icon: categoryTileIcon(c.id),
        tint: typeof c.icon?.tint === 'string' ? c.icon.tint : 'var(--surface-2)',
        // A registry tint without a glyph colour is a solid brand fill: draw the icon white on it.
        glyphColor:
          typeof c.icon?.glyph_color === 'string'
            ? c.icon.glyph_color
            : typeof c.icon?.tint === 'string'
              ? '#fff'
              : 'var(--text-2)',
        onPick: () => pickCategory(c.id),
      })),
    ];
    rows.push(...catRows.filter((r) => !q || r.label.toLowerCase().includes(q)));
  }
  if (tab === 'all' || tab === 'biz') {
    for (const b of biz.items.slice(0, 6)) {
      const cat = categories.find((c) => c.id === b.category_id);
      rows.push({
        key: `b-${b.id}`,
        kind: 'biz',
        label: b.name,
        sub: [cat ? (lang === 'bn' ? cat.label_bn : cat.label_en) : '', b.area_name].filter(Boolean).join(' · '),
        image: b.cover_image?.['400'] ?? b.cover_image?.['800'],
        onPick: () => go(`/b/${b.slug}`),
      });
    }
  }
  if (tab === 'all' || tab === 'pros') {
    for (const p of pros.items.slice(0, 6)) {
      rows.push({
        key: `p-${p.id}`,
        kind: 'pro',
        label: p.name,
        sub: [p.role_title, p.business.name, p.business.area_name].filter(Boolean).join(' · '),
        image: p.photo_url?.['400'] ?? p.photo_url?.['800'],
        onPick: () => go(`/b/${p.business.slug}`),
      });
    }
  }
  const catCount = categories.filter((c) => !q || catName(c).toLowerCase().includes(q)).length;

  function go(to: string) {
    setOpen(null);
    setMobileStep(null);
    navigate(to);
  }
  function pickCategory(id: string | null) {
    setCategoryId(id);
    setQuery('');
    setKb(-1);
    setOpen(null);
    if (mobileStep) setMobileStep('loc');
  }
  function pickPlace(p: PlaceOption | null) {
    setPlace(p);
    setLocFilter('');
    setOpen(null);
    if (mobileStep) setMobileStep('when');
    if (p) {
      const next = pushRecentLocation(recentIds, p.id);
      setRecentIds(next);
      saveRecentLocations(next);
    }
  }
  function windowParam(): string | null {
    if (when.chip === 'custom') return `${when.from}-${Math.max(when.from + 1, when.to)}`;
    if (when.chip === 'any') return null;
    return when.chip;
  }
  function submit() {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (categoryId) params.set('category', categoryId);
    if (place) params.set('location', place.slug);
    if (when.date) params.set('available_on', when.date);
    const win = windowParam();
    if (win) params.set('window', win);
    setOpen(null);
    setMobileStep(null);
    navigate(`/search?${params.toString()}`);
  }
  function onServiceKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setKb((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setKb((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (kb >= 0 && rows[kb]) rows[kb]!.onPick();
      else submit();
    }
  }

  const category = categories.find((c) => c.id === categoryId);
  const serviceLabel = category ? catName(category) : query || t('dc50.hero.all_services');
  const placeLabel = place ? (place.isCity ? place.label : `${place.label}, ${place.cityLabel}`) : t('dc50.hero.current_location');
  const timePart = (() => {
    if (when.chip === 'custom') return `${formatClock(lang, when.from, 0)}–${formatClock(lang, Math.max(when.from + 1, when.to), 0)}`;
    if (when.chip === 'any') return null;
    return t(`dc50.when.short_${when.chip}`);
  })();
  const whenSummary = [when.date ? dateLabel(new Date(`${when.date}T12:00:00`)) : null, timePart].filter(Boolean).join(' · ');
  const recent = recentIds.map((id) => places.find((p) => p.id === id)).filter((p): p is PlaceOption => Boolean(p));
  const closeMobile = useCallback(() => setMobileStep(null), []);
  const toggle = (s: Segment) => {
    setKb(-1);
    setOpen((cur) => (cur === s ? null : s));
  };

  return (
    <>
      <button type="button" className="hm-hero__mbar" onClick={() => {
          setKb(-1);
          setMobileStep('service');
        }} aria-label={t('dc50.hero.open_search')}>
        <span className="hm-hero__mbar-txt">
          <span className="hm-hero__mbar-title">{serviceLabel}</span>
          <span className="hm-hero__mbar-sub">
            <PinIcon size={11} stroke={2.4} />
            <span>
              {placeLabel} · {whenSummary || t('dc50.hero.any_time')}
            </span>
          </span>
        </span>
        <span className="hm-hero__mbar-go" aria-hidden="true">
          <SearchIcon size={18} />
        </span>
      </button>

      <div className="hm-hero__wrap" ref={wrapRef}>
        <div className="hm-pill">
          {open === 'service' ? (
            <div className="hm-seg hm-seg--svc is-on">
              <span className="hm-seg__ic">
                <SearchIcon />
              </span>
              <input
                className="hm-seg__input"
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setCategoryId(null);
                  setKb(-1);
                }}
                onKeyDown={onServiceKey}
                placeholder={t('dc50.hero.all_services')}
                aria-label={t('dc50.hero.service_label')}
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-activedescendant={kb >= 0 ? `${listId}-${kb}` : undefined}
              />
            </div>
          ) : (
            <button type="button" className="hm-seg hm-seg--svc" aria-expanded={false} onClick={() => toggle('service')}>
              <span className="hm-seg__ic">
                <SearchIcon />
              </span>
              <span className={category || query ? 'hm-seg__txt' : 'hm-seg__txt is-ph'}>{serviceLabel}</span>
            </button>
          )}
          <button
            type="button"
            className={open === 'loc' ? 'hm-seg hm-seg--loc is-on' : 'hm-seg hm-seg--loc'}
            aria-expanded={open === 'loc'}
            aria-label={`${t('dc50.hero.location_label')}: ${placeLabel}`}
            onClick={() => toggle('loc')}
          >
            <span className="hm-seg__ic">
              <PinIcon />
            </span>
            <span className={place ? 'hm-seg__txt' : 'hm-seg__txt is-ph'}>{placeLabel}</span>
          </button>
          <button
            type="button"
            className={open === 'when' ? 'hm-seg hm-seg--when is-on' : 'hm-seg hm-seg--when'}
            aria-expanded={open === 'when'}
            aria-label={`${t('dc50.hero.when_label')}: ${whenSummary || t('dc50.hero.any_time')}`}
            onClick={() => toggle('when')}
          >
            <span className="hm-seg__ic">
              <CalendarIcon />
            </span>
            <span className={whenSummary ? 'hm-seg__txt' : 'hm-seg__txt is-ph'}>{whenSummary || t('dc50.hero.any_time')}</span>
          </button>
          <button type="button" className="hm-pill__go" onClick={submit}>
            {t('dc50.hero.search')} →
          </button>
        </div>

        {open === 'service' ? (
          <HeroServicePanel
            query={query}
            tab={tab}
            onTab={(x) => {
              setTab(x);
              setKb(-1);
            }}
            counts={{ cats: catCount, biz: biz.total, pros: pros.total }}
            rows={rows}
            active={kb}
            listId={listId}
          />
        ) : null}
        {open === 'loc' ? (
          <HeroLocationPanel
            places={places}
            recent={recent}
            filter={locFilter}
            onFilter={setLocFilter}
            onPick={pickPlace}
            onClear={() => pickPlace(null)}
          />
        ) : null}
        {open === 'when' ? (
          <HeroWhenPanel value={when} onChange={setWhen} month={calMonth} onMonth={setCalMonth} now={now} onApply={() => setOpen(null)} />
        ) : null}
      </div>

      {/* The flow renders on <body>: the home page draws inside the design scope (.hx),
          whose element resets (.hx button) would restyle the flow's own controls. */}
      {mobileStep
        ? createPortal(
        <Suspense fallback={null}>
        <MobileSearchFlow
          step={mobileStep}
          onStep={setMobileStep}
          onClose={closeMobile}
          onSearch={submit}
          query={query}
          onQuery={(v) => {
            setQuery(v);
            setCategoryId(null);
          }}
          tab={tab}
          onTab={setTab}
          counts={{ cats: catCount, biz: biz.total, pros: pros.total }}
          rows={rows}
          places={places}
          recent={recent}
          locFilter={locFilter}
          onLocFilter={setLocFilter}
          onPickPlace={pickPlace}
          when={when}
          onWhen={setWhen}
          month={calMonth}
          onMonth={setCalMonth}
          now={now}
        />
        </Suspense>,
            document.body,
          )
        : null}
    </>
  );
}
